import useApi from '../../../api/useApi'
import ConfigurationContext from '../../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { VideoSession } from '../../video/VideoSession'
import { useCreateSession } from '../useCreateSession'
import { type UploadFormat } from '../UploadFormat'
import { useTrackSessionContext } from '../../trackSession/TrackSessionContext'
import { extract } from './extracts/gpmf-extract'
import { GOPRO } from './goProDevice'
import { parseGoProToRawTelemetry } from './parseGoProTelemetry'
import { rememberRawTelemetry } from '../rawTelemetrySource'

const NO_GPS =
  'No GPS data found in the file. Make sure GPS was enabled on the camera and had a satellite lock during the recording.'

export const useGoProProcessor = (
  setProgressPercent: React.Dispatch<React.SetStateAction<number>>
): UploadFormat => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { setVideoSessions } = useTrackSessionContext()
  const createSession = useCreateSession(api)

  const addAsVideo = (sessionId: string, file: File): void => {
    setVideoSessions((prevState) => [
      ...prevState,
      new VideoSession(sessionId, file),
    ])
  }

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    const progress = (percent: number) => {
      setProgressPercent(percent)
    }

    const gpmfTrack = await extract(file, { progress })

    rememberRawTelemetry(sessionId, {
      fileName: `${file.name}.gpmf`,
      contentType: 'application/octet-stream',
      content: new Blob([gpmfTrack.rawData]),
    })

    const rawTelemetry = await parseGoProToRawTelemetry([gpmfTrack])
    const created = await createSession(rawTelemetry, {
      sessionId,
      fileName: file.name,
      device: GOPRO,
      sessionFeatures: { hasLeanAngle: true, hasVideo: true },
    })
    if (!created) throw new Error(NO_GPS)

    addAsVideo(sessionId, file)
  }

  return {
    extensions: ['.MP4', '.LRV'],
    keepsFileAsSource: false,
    processFile,
    addAsVideo,
    error: api.error,
  }
}
